import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  Box,
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Alert,
  Chip,
  Button,
  Divider,
  Switch,
} from '@mui/material';
import {
  InfoOutlined,
  VisibilityRounded,
  CodeRounded,
  UnfoldLessRounded,
  UnfoldMoreRounded,
} from '@mui/icons-material';
import { BlockEditor } from './BlockEditor';
import { blocksToHtml, buildPreviewHtml, buildStarterBlocks } from './utils';
import type { EmailBlock, CampaignDraft } from './types';

// ─── Template catalog (content-only) ─────────────────────────────────────────
// EL NOMBRE SE DERIVA DEL id, no se escribe acá: lo lee el usuario, y una
// constante de módulo no puede llamar a `t`. La pantalla lo resuelve al
// dibujar, y así una plantilla nueva sólo necesita su clave.
const TEMPLATES = [{ id: 'blank' }, { id: 'newsletter' }, { id: 'alert' }] as const;

// ─── Props ────────────────────────────────────────────────────────────────────
interface Step1ContentProps {
  draft: CampaignDraft;
  tenantName: string;
  primaryColor: string;
  logoUrl?: string;
  tenantId: string;
  onChange: (patch: Partial<CampaignDraft>) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────
export function Step1Content({
  draft,
  tenantName,
  primaryColor,
  logoUrl,
  tenantId,
  onChange,
}: Step1ContentProps) {
  const { t } = useTranslation('sis');
  const [restoreAsk, setRestoreAsk] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<string | null>(null);
  // EL MODO Y LA COPIA VIVEN EN EL BORRADOR, no acá: este componente se
  // desmonta al pasar a Audiencia y volver, y un `useState`/`useRef` se habría
  // perdido justo en el camino que más trabajo cuesta reconstruir.
  const showRawHtml = draft.rawHtmlMode === true;
  const blocksBeforeRaw = draft.blocksBeforeRaw ?? null;
  const rawTouched = showRawHtml && draft.blocks.length === 1 && draft.blocks[0]?.id === 'raw';
  const [allExpanded, setAllExpanded] = useState<boolean | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState('blank');

  const bodyHtml = useMemo(() => blocksToHtml(draft.blocks), [draft.blocks]);

  const previewHtml = useMemo(
    () =>
      buildPreviewHtml({
        tenantName,
        primaryColor,
        ...(logoUrl ? { logoUrl } : {}),
        bodyHtml,
        subject: draft.subject,
      }),
    [tenantName, primaryColor, logoUrl, bodyHtml, draft.subject],
  );

  /** Cambiar de plantilla REEMPLAZA todo el contenido. Con algo escrito, se
   *  pregunta: antes lo pisaba sin aviso y sin deshacer. */
  function handleTemplateChange(templateId: string) {
    const hayTrabajo = draft.blocks.some(
      (b) => 'content' in b && String((b as { content?: string }).content ?? '').trim() !== '',
    );
    if (hayTrabajo) {
      setPendingTemplate(templateId);
      return;
    }
    applyTemplate(templateId);
  }

  function applyTemplate(templateId: string) {
    setSelectedTemplate(templateId);
    onChange({ blocks: buildStarterBlocks(templateId, tenantName, primaryColor) });
  }

  function handleBlocksChange(blocks: EmailBlock[]) {
    onChange({ blocks });
  }

  /** Editar el HTML colapsa los bloques en uno solo — pero ya no sin retorno.
   *
   *  LO QUE PASABA ANTES: al escribir UN SOLO CARÁCTER acá, los bloques
   *  estructurados se fundían en un bloque de texto y no había forma de
   *  volver. Sin aviso, sin deshacer, y con el botón a un clic del editor
   *  visual. Quien entraba «a mirar el HTML» perdía media hora de trabajo.
   *
   *  El colapso sigue siendo necesario —el HTML libre no se puede repartir en
   *  bloques— pero ahora se guarda la estructura anterior y se ofrece volver. */
  function handleRawHtmlChange(html: string) {
    onChange({ blocks: [{ id: 'raw', type: 'text', content: html, align: 'left' }] });
  }

  /** Entrar y salir del modo HTML sin perder lo construido. */
  function toggleRawHtml() {
    if (!showRawHtml) {
      onChange({ rawHtmlMode: true, blocksBeforeRaw: draft.blocks });
      return;
    }
    if (rawTouched && blocksBeforeRaw && blocksBeforeRaw.length > 1) {
      setRestoreAsk(true);
      return;
    }
    onChange({ rawHtmlMode: false });
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 3,
        minHeight: 600,
        alignItems: 'start',
      }}
    >
      {/* ── LEFT PANEL: Editor ── */}
      <Stack spacing={2.5}>
        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
          {t('campaign.step1.emailDesign')}
        </Typography>

        {/* Template selector */}
        <FormControl fullWidth size="small">
          <InputLabel>{t('campaign.editor2.template')}</InputLabel>
          <Select
            value={selectedTemplate}
            label={t('campaign.editor2.template')}
            onChange={(e) => handleTemplateChange(e.target.value)}
          >
            {TEMPLATES.map((tpl) => (
              <MenuItem key={tpl.id} value={tpl.id}>
                {t(`campaign.templates.${tpl.id}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Subject */}
        <TextField
          label={t('campaign.step1.subject')}
          value={draft.subject}
          onChange={(e) => onChange({ subject: e.target.value })}
          fullWidth
          required
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={t('campaign.step1.subjectHint')}>
                  <InfoOutlined fontSize="small" sx={{ color: 'text.disabled' }} />
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />

        {/* Merge field hint */}
        <Alert severity="info" icon={<InfoOutlined fontSize="small" />} sx={{ py: 0.5 }}>
          <Typography variant="body2">
            <strong>{t('campaign.step1.availableFields')}</strong>{' '}
            <Box
              component="code"
              sx={{
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '4px',
                px: 0.75,
                py: 0.25,
                fontFamily: 'monospace',
                fontSize: 12,
              }}
            >
              {'{{nombre_primero}}'}
            </Box>{' '}
            ·{' '}
            <Box
              component="code"
              sx={{
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '4px',
                px: 0.75,
                py: 0.25,
                fontFamily: 'monospace',
                fontSize: 12,
              }}
            >
              {'{{nombre}}'}
            </Box>{' '}
            ·{' '}
            <Box
              component="code"
              sx={{
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '4px',
                px: 0.75,
                py: 0.25,
                fontFamily: 'monospace',
                fontSize: 12,
              }}
            >
              {'{{correo}}'}
            </Box>
          </Typography>
        </Alert>

        <Divider />

        {/* Single unified toolbar: label  |  HTML avanzado  |  collapse toggle */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {t('campaign.step1.contentBlocks')}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {/* HTML avanzado toggle */}
          <Tooltip
            title={showRawHtml ? t('campaign.editor2.toVisual') : t('campaign.editor2.toRaw')}
          >
            <Button
              size="small"
              variant="text"
              startIcon={
                showRawHtml ? (
                  <VisibilityRounded fontSize="small" />
                ) : (
                  <CodeRounded fontSize="small" />
                )
              }
              onClick={toggleRawHtml}
              sx={{ fontSize: 12, textTransform: 'none', minWidth: 0 }}
            >
              {showRawHtml ? 'Editor visual' : 'HTML avanzado'}
            </Button>
          </Tooltip>
          {/* Separator */}
          {!showRawHtml && (
            <>
              <Typography variant="caption" color="divider">
                |
              </Typography>
              {/* Collapse / Expand all toggle */}
              <Tooltip title={allExpanded === true ? 'Colapsar todos' : 'Expandir todos'}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <UnfoldLessRounded
                    sx={{
                      fontSize: 16,
                      opacity: allExpanded === true ? 1 : 0.35,
                      color: 'text.secondary',
                    }}
                  />
                  <Switch
                    size="small"
                    checked={allExpanded === true}
                    onChange={(_, checked) => setAllExpanded(checked)}
                  />
                  <UnfoldMoreRounded
                    sx={{
                      fontSize: 16,
                      opacity: allExpanded === true ? 0.35 : 1,
                      color: 'text.secondary',
                    }}
                  />
                </Stack>
              </Tooltip>
            </>
          )}
        </Stack>

        {/* Block editor or raw HTML */}
        {showRawHtml && (
          <TextField
            value={bodyHtml}
            onChange={(e) => handleRawHtmlChange(e.target.value)}
            fullWidth
            multiline
            rows={16}
            size="small"
            placeholder={t('campaign.step1.rawHtmlPlaceholder')}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
            helperText={t('campaign.step1.bodyHelp')}
          />
        )}
        {showRawHtml && (blocksBeforeRaw?.length ?? 0) > 1 && (
          <Alert severity={rawTouched ? 'warning' : 'info'} sx={{ mt: 1 }}>
            {rawTouched ? t('campaign.editor.rawWarnAfter') : t('campaign.editor.rawWarnBefore')}
          </Alert>
        )}
        {!showRawHtml && (
          <BlockEditor
            blocks={draft.blocks}
            primaryColor={primaryColor}
            tenantId={tenantId}
            allExpanded={allExpanded}
            onChange={handleBlocksChange}
          />
        )}
      </Stack>

      {/* ── RIGHT PANEL: Live Preview ── */}
      <Box sx={{ position: 'sticky', top: 16 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">
            {t('campaign.step1.preview')}
          </Typography>
          <Chip
            label={t('campaign.step1.livePreview')}
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: 11 }}
          />
        </Stack>

        {/* Subject preview bar */}
        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderBottom: 'none',
            borderRadius: '8px 8px 0 0',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Asunto:{' '}
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            {draft.subject || t('campaign.draft.noSubject')}
          </Typography>
        </Box>

        {/* Email iframe */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '0 0 8px 8px',
            overflow: 'hidden',
            bgcolor: '#f4f6f9',
          }}
        >
          <iframe
            srcDoc={previewHtml}
            style={{ width: '100%', minHeight: 520, border: 'none', display: 'block' }}
            title={t('campaign.step1.previewTitle')}
            sandbox="allow-scripts"
          />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {/* El nombre del campo va DENTRO de la traducción: en otro idioma la
              frase puede colocarlo en otro sitio, y cosiendo dos mitades se
              impone un orden que no todos admiten. */}
          <Trans
            i18nKey="campaign.step1.previewFieldsNote"
            ns="sis"
            // EL MARCADOR VIAJA COMO VALOR, no dentro del texto traducible:
            // `{{nombre}}` escrito en la traducción lo interpretaría i18next
            // como una variable y lo borraría. Pasado así se imprime tal cual
            // —i18next no vuelve a interpolar lo que inserta— y además el
            // marcador queda fuera de lo que hay que traducir, que es correcto:
            // el nombre del campo es el mismo en todos los idiomas.
            values={{ campo: '{{nombre}}' }}
            components={{ code: <code /> }}
          />
        </Typography>
      </Box>

      <Dialog open={restoreAsk} onClose={() => setRestoreAsk(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('campaign.editor.restoreTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('campaign.editor.restoreBody', { count: blocksBeforeRaw?.length ?? 0 })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRestoreAsk(false);
              onChange({ rawHtmlMode: false });
            }}
          >
            {t('campaign.editor.restoreKeepHtml')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setRestoreAsk(false);
              onChange({
                rawHtmlMode: false,
                ...(blocksBeforeRaw ? { blocks: blocksBeforeRaw } : {}),
              });
            }}
          >
            {t('campaign.editor.restoreBlocks')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pendingTemplate !== null}
        onClose={() => setPendingTemplate(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('campaign.editor.templateTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('campaign.editor.templateBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingTemplate(null)}>{t('campaign.editor.cancel')}</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              if (pendingTemplate) applyTemplate(pendingTemplate);
              setPendingTemplate(null);
            }}
          >
            {t('campaign.editor.templateConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
